import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { 
  Save, 
  ArrowLeft,
  MapPin,
  Building2,
  Truck,
  Package,
  Factory,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { it } from "date-fns/locale";
import { getDDRById, updateDDR, getAllSpecies, getAllCausali } from "@/services/localDatabase";
import { downloadDDRPdf } from "@/services/pdfGenerator";

const EditDDR = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [species, setSpecies] = useState([]);
  const [causali, setCausali] = useState([]);
  const [ddrInfo, setDdrInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    produttore: null,
    zona_produzione: "ALLEVAMENTO",
    data_raccolta: "",
    stato_sanitario: "B",
    ubicazione_zona: "",
    provenienza: null,
    specie: "",
    n_colli: 0,
    peso_kg: 0,
    lotto: "",
    destinazione: {
      tipo: "depurazione",
      numero_riconoscimento: "",
      nome: "",
      via: "",
      comune: "",
      provincia: ""
    },
    identificazione_vettore: "",
    causale_text: "",
    data_documento: ""
  });

  const [dataRaccolta, setDataRaccolta] = useState(new Date());
  const [dataDocumento, setDataDocumento] = useState(new Date());
  const [dataIngresso, setDataIngresso] = useState(null);
  const [dataUscita, setDataUscita] = useState(null);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date();
    try {
      return parse(dateStr, "dd/MM/yyyy", new Date());
    } catch {
      return new Date();
    }
  };

  const fetchInitialData = async () => {
    try {
      const [speciesData, causaliData, ddr] = await Promise.all([
        getAllSpecies(),
        getAllCausali(),
        getDDRById(id)
      ]);
      
      if (!ddr) {
        toast.error("DDR non trovato");
        navigate("/storico");
        return;
      }
      
      setSpecies(speciesData);
      setCausali(causaliData);
      setDdrInfo({ numero_ddr: ddr.numero_ddr, serie: ddr.serie });
      
      setFormData({
        produttore: ddr.produttore || null,
        zona_produzione: ddr.zona_produzione || "ALLEVAMENTO",
        data_raccolta: ddr.data_raccolta || "",
        stato_sanitario: ddr.stato_sanitario || "B",
        ubicazione_zona: ddr.ubicazione_zona || "",
        provenienza: ddr.provenienza || null,
        specie: ddr.specie || "",
        n_colli: ddr.n_colli || 0,
        peso_kg: ddr.peso_kg || 0,
        lotto: ddr.lotto || "",
        destinazione: ddr.destinazione || {
          tipo: "depurazione",
          numero_riconoscimento: "",
          nome: "",
          via: "",
          comune: "",
          provincia: ""
        },
        identificazione_vettore: ddr.identificazione_vettore || "",
        causale_text: ddr.causale_text || "",
        data_documento: ddr.data_documento || ""
      });
      
      setDataRaccolta(parseDate(ddr.data_raccolta));
      setDataDocumento(parseDate(ddr.data_documento));
      
      if (ddr.provenienza?.data_ingresso) {
        setDataIngresso(parseDate(ddr.provenienza.data_ingresso));
      }
      if (ddr.provenienza?.data_uscita) {
        setDataUscita(parseDate(ddr.provenienza.data_uscita));
      }
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento del DDR");
      navigate("/storico");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleTipoChange = (tipo) => {
    setFormData(prev => ({
      ...prev,
      produttore: { ...prev.produttore, tipo: tipo }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        provenienza: formData.provenienza,
        data_raccolta: format(dataRaccolta, "dd/MM/yyyy"),
        data_documento: format(dataDocumento, "dd/MM/yyyy")
      };

      await updateDDR(id, submitData);
      toast.success(`DDR ${ddrInfo.serie}-${ddrInfo.numero_ddr} aggiornato con successo!`);
      navigate("/storico");
    } catch (error) {
      console.error("Error updating DDR:", error);
      toast.error("Errore nell'aggiornamento del DDR");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const ddr = await getDDRById(id);
      await downloadDDRPdf(ddr);
      toast.success("PDF scaricato");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Errore nel download del PDF");
    }
  };

  if (initialLoading) {
    return (
      <div className="p-6 md:p-8 lg:p-12 flex items-center justify-center min-h-[50vh]">
        <p className="text-slate-500">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="edit-ddr-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/storico")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
              Modifica DDR {ddrInfo?.serie}-{ddrInfo?.numero_ddr}
            </h1>
            <p className="text-slate-500 mt-1">Modifica i dati del documento</p>
          </div>
        </div>
        <Button onClick={handleDownloadPDF} variant="outline" data-testid="download-pdf-btn">
          <Download className="w-4 h-4 mr-2" />
          Scarica PDF
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl space-y-6">
          {/* Produttore */}
          <Card className="border-slate-100 border-orange-200 bg-orange-50/30">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Factory className="w-5 h-5 text-orange-500" />
                Produttore
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.produttore && (
                <div className="p-3 bg-white rounded border border-slate-200 text-sm">
                  <p className="font-medium">{formData.produttore.nome}</p>
                  <p className="text-slate-500">{formData.produttore.sede_legale}, {formData.produttore.cap} {formData.produttore.citta} ({formData.produttore.provincia})</p>
                  <p className="text-slate-500">
                    RIC: {formData.produttore.ric}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Zona di Produzione */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <MapPin className="w-5 h-5 text-orange-500" />
                Zona di Produzione
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={formData.zona_produzione}
                onValueChange={(value) => handleInputChange("zona_produzione", value)}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ALLEVAMENTO" id="allevamento" />
                  <Label htmlFor="allevamento" className="font-normal">Allevamento</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="LIBERA_RACCOLTA" id="libera" />
                  <Label htmlFor="libera" className="font-normal">Libera Raccolta</Label>
                </div>
              </RadioGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Data di Raccolta</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal mt-1">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(dataRaccolta, "dd/MM/yyyy", { locale: it })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={dataRaccolta} onSelect={(date) => date && setDataRaccolta(date)} locale={it} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Stato Sanitario</Label>
                  <Select value={formData.stato_sanitario} onValueChange={(value) => handleInputChange("stato_sanitario", value)}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - Classe A</SelectItem>
                      <SelectItem value="B">B - Classe B</SelectItem>
                      <SelectItem value="C">C - Classe C</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Ubicazione Zona</Label>
                <Input
                  value={formData.ubicazione_zona}
                  onChange={(e) => handleInputChange("ubicazione_zona", e.target.value)}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Specie e Quantità */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Package className="w-5 h-5 text-orange-500" />
                Specie e Quantità
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Specie</Label>
                <Select value={formData.specie} onValueChange={(value) => handleInputChange("specie", value)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Seleziona specie" /></SelectTrigger>
                  <SelectContent>
                    {species.map((s) => (
                      <SelectItem key={s.id} value={s.nome_scientifico ? `${s.nome_comune} (${s.nome_scientifico})` : s.nome_comune}>
                        {s.nome_comune} {s.nome_scientifico ? `(${s.nome_scientifico})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={formData.specie}
                  onChange={(e) => handleInputChange("specie", e.target.value.toUpperCase())}
                  placeholder="Oppure digita manualmente"
                  className="mt-2"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">N. Colli</Label>
                  <Input type="number" value={formData.n_colli} onChange={(e) => handleInputChange("n_colli", parseInt(e.target.value) || 0)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Peso (Kg)</Label>
                  <Input type="number" step="0.1" value={formData.peso_kg} onChange={(e) => handleInputChange("peso_kg", parseFloat(e.target.value) || 0)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Lotto</Label>
                  <Input value={formData.lotto} onChange={(e) => handleInputChange("lotto", e.target.value)} className="mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Destinazione */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Building2 className="w-5 h-5 text-orange-500" />
                Destinazione
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={formData.destinazione?.tipo || "depurazione"} onValueChange={(value) => handleNestedChange("destinazione", "tipo", value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="depurazione">Centro di Depurazione</SelectItem>
                  <SelectItem value="spedizione">Centro di Spedizione</SelectItem>
                  <SelectItem value="trasformazione">Centro di Trasformazione</SelectItem>
                  <SelectItem value="operatore_intermedio">Operatore Intermedio</SelectItem>
                  <SelectItem value="zona_produzione">Zona di Produzione</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Numero Riconoscimento" value={formData.destinazione?.numero_riconoscimento || ""} onChange={(e) => handleNestedChange("destinazione", "numero_riconoscimento", e.target.value)} />
              <Input placeholder="Nome / Denominazione" value={formData.destinazione?.nome || ""} onChange={(e) => handleNestedChange("destinazione", "nome", e.target.value)} />
              <Input placeholder="Via e Numero" value={formData.destinazione?.via || ""} onChange={(e) => handleNestedChange("destinazione", "via", e.target.value)} />
              <div className="grid grid-cols-2 gap-4">
                <Input placeholder="Comune" value={formData.destinazione?.comune || ""} onChange={(e) => handleNestedChange("destinazione", "comune", e.target.value)} />
                <Input placeholder="Provincia" maxLength={2} value={formData.destinazione?.provincia || ""} onChange={(e) => handleNestedChange("destinazione", "provincia", e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Vettore e Data */}
          <Card className="border-slate-100">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-barlow">
                <Truck className="w-5 h-5 text-orange-500" />
                Vettore e Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Identificazione Vettore</Label>
                <Input value={formData.identificazione_vettore} onChange={(e) => handleInputChange("identificazione_vettore", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Causale Trasporto (DDT)</Label>
                {causali.length > 0 ? (
                  <Select
                    value={formData.causale_text}
                    onValueChange={(value) => handleInputChange("causale_text", value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-select-causale">
                      <SelectValue placeholder="Seleziona causale" />
                    </SelectTrigger>
                    <SelectContent>
                      {causali.map((c) => (
                        <SelectItem key={c.id} value={c.testo}>
                          {c.testo}{c.predefinito ? " ★" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData.causale_text}
                    onChange={(e) => handleInputChange("causale_text", e.target.value.toUpperCase())}
                    placeholder="es. RESTITUZIONE C/ALLEVAMENTO"
                    className="mt-1"
                  />
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Data Documento</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal mt-1">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(dataDocumento, "dd/MM/yyyy", { locale: it })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dataDocumento} onSelect={(date) => date && setDataDocumento(date)} locale={it} />
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-12 text-base"
            data-testid="save-ddr-btn"
          >
            {loading ? "Salvataggio in corso..." : (
              <><Save className="w-5 h-5 mr-2" />Salva Modifiche</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditDDR;
